import { useState } from "react";
import type { GenerationResult } from "../types";

interface Props {
  results: GenerationResult[];
}

export function ResultsGallery({ results }: Props) {
  const [filter, setFilter] = useState("");

  const successfulResults = results.filter((r) => !r.error);
  const failedResults = results.filter((r) => r.error);

  const filteredResults = filter
    ? successfulResults.filter(
        (r) =>
          r.id.toLowerCase().includes(filter.toLowerCase()) ||
          r.engine.toLowerCase().includes(filter.toLowerCase())
      )
    : successfulResults;

  const handleDownload = async (result: GenerationResult) => {
    if (!result.imageUrl) return;

    try {
      const response = await fetch(result.imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.jobId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Generated Images ({successfulResults.length})
        </h2>
        <input
          type="text"
          placeholder="Filter results..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {failedResults.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-700">
            {failedResults.length} generation(s) failed
          </p>
          <ul className="mt-2 text-sm text-red-600">
            {failedResults.map((r) => (
              <li key={r.id}>
                {r.id}: {r.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {filteredResults.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          {filter ? "No results match your filter" : "No images generated yet"}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResults.map((result) => (
            <div
              key={result.jobId}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {result.imageUrl && (
                <div className="aspect-square bg-gray-100">
                  <img
                    src={result.imageUrl}
                    alt={`Generated art for ${result.id}`}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              )}

              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                    {result.engine}
                  </span>
                  <span className="text-xs text-gray-500 truncate">
                    {result.jobId}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownload(result)}
                    className="flex-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => copyToClipboard(result.jobId)}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    title="Copy Job ID"
                  >
                    Copy ID
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
